import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  FiMenu, 
  FiX, 
  FiShoppingCart, 
  FiUser, 
  FiLogOut, 
  FiChevronDown, 
  FiHeart, 
  FiSearch, 
  FiLogIn,
  FiShoppingBag
} from 'react-icons/fi';
// eslint-disable-next-line no-unused-vars -- motion is used in JSX as <motion.div>
import { AnimatePresence, motion } from 'framer-motion';
import CartSidebar from './CartSidebar';
import { userApi, BASE_URL } from '../../api/userApi';

const Navbar = ({ onLogout }) => {
  const [categories, setCategories] = useState([]);
  const [collections, setCollections] = useState([]);
  const [logo, setLogo] = useState('');
  const [loadingStates, setLoadingStates] = useState({
    nav: true,
    logo: true
  });
  const [userProfile, setUserProfile] = useState({
    full_name: '',
    email: ''
  });
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Check authentication status
  useEffect(() => {
    const checkAuth = () => {
      const token = localStorage.getItem('token');
      setIsAuthenticated(!!token);
    };
    
    checkAuth();
    
    // Listen for auth changes
    window.addEventListener('storage', checkAuth);
    return () => window.removeEventListener('storage', checkAuth);
  }, []);

  // Fetch categories and collections
  useEffect(() => {
    const fetchNavData = async () => {
      try {
        const [categoriesData, collectionsData] = await Promise.all([
          userApi.getCategories(),
          userApi.getCollections()
        ]);

        // Filter active categories
        const activeCategories = categoriesData.filter(category => category.is_active);
        setCategories(activeCategories);

        // Filter active collections (tags)
        const activeCollections = collectionsData.filter(collection => collection.is_active);
        setCollections(activeCollections);
      } catch (error) {
        console.error('Error fetching navigation data:', error);
      } finally {
        setLoadingStates(prev => ({ ...prev, nav: false }));
      }
    };

    fetchNavData();
  }, []);

  // Fetch logo
  useEffect(() => {
    const fetchLogo = async () => {
      try {
        const response = await userApi.getLogo();
        if (response?.image) {
          let logoUrl = response.image;
          // If URL is relative (starts with /), prepend API domain
          if (logoUrl && logoUrl.startsWith('/') && !logoUrl.startsWith('http')) {
            const apiDomain = BASE_URL.replace('/api', '');
            logoUrl = `${apiDomain}${logoUrl}`;
          }
          // If URL doesn't start with http/https/data, it might be a relative path
          else if (logoUrl && !logoUrl.startsWith('http') && !logoUrl.startsWith('data:') && !logoUrl.startsWith('blob:')) {
            const apiDomain = BASE_URL.replace('/api', '');
            logoUrl = logoUrl.startsWith('/') ? `${apiDomain}${logoUrl}` : `${apiDomain}/${logoUrl}`;
          }
          setLogo(logoUrl);
        }
      } catch (error) {
        console.error('Error fetching logo:', error);
      } finally {
        setLoadingStates(prev => ({ ...prev, logo: false }));
      }
    };

    fetchLogo();
  }, []);

  // Fetch user profile
  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        const response = await userApi.getProfile();
        setUserProfile({
          full_name: response.full_name,
          email: response.email
        });
      } catch (error) {
        console.error('Error fetching user profile:', error);
        // Handle unauthorized access
        if (error.message.includes('401')) {
          localStorage.removeItem('token');
          setIsAuthenticated(false);
          onLogout?.();
        }
      }
    };

    // Only fetch if user is authenticated
    if (isAuthenticated) {
      fetchUserProfile();
    }
  }, [isAuthenticated, onLogout]);

  // Replace the static shopDropdownData with dynamic data
  const shopDropdownData = {
    categories: categories.map(category => ({
      name: category.name,
      link: `/products?category=${encodeURIComponent(category.name)}`
    })),
    collections: collections.map(collection => ({
      name: collection.name,
      link: `/products?filter=${encodeURIComponent(collection.name.toLowerCase())}`
    }))
  };

  // Update wishlist count state and logic
  const [wishlistCount, setWishlistCount] = useState(0);
  const [cartCount, setCartCount] = useState(0);

  // Update wishlist count when API data changes
  useEffect(() => {
    const updateWishlistCount = async () => {
      try {
        const wishlistData = await userApi.getWishlist();
        setWishlistCount(wishlistData.length);
      } catch (error) {
        console.error('Error updating wishlist count:', error);
        setWishlistCount(0);
      }
    };

    // Initial count
    updateWishlistCount();

    // Listen for wishlist updates
    window.addEventListener('wishlistUpdated', updateWishlistCount);

    return () => {
      window.removeEventListener('wishlistUpdated', updateWishlistCount);
    };
  }, []);

  // Update cart count when API data changes
  useEffect(() => {
    const updateCartCount = async () => {
      try {
        const cartItems = await userApi.getCartItems();
        // Count unique products in cart, not their quantities
        const totalUniqueProducts = cartItems.length;
        setCartCount(totalUniqueProducts);
      } catch (error) {
        console.error('Error updating cart count:', error);
        setCartCount(0);
      }
    };

    // Initial count
    updateCartCount();

    // Listen for cart updates
    window.addEventListener('cartUpdated', updateCartCount);

    return () => {
      window.removeEventListener('cartUpdated', updateCartCount);
    };
  }, []);

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isShopDropdownOpen, setIsShopDropdownOpen] = useState(false);
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  // Remove these states as they're no longer needed
  // const [recentSearches] = useState(['Summer Dress', 'Silk Saree', 'Wedding Collection']);
  // const [trendingSearches] = useState(['Festival Collection', 'Designer Wear', 'Traditional Wear']);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [mobileCollectionsOpen, setMobileCollectionsOpen] = useState(false);
  const [mobileCategoriesOpen, setMobileCategoriesOpen] = useState(false);

  const navigate = useNavigate();

  const handleLogoutClick = () => {
    setShowLogoutConfirm(true);
    setIsProfileDropdownOpen(false);
  };

  const handleConfirmLogout = () => {
    setShowLogoutConfirm(false);
    // Remove token from localStorage
    localStorage.removeItem('token');
    // Call the onLogout prop to update the auth state in App.jsx
    onLogout();
  };

  const handleSearchOpen = () => {
    setIsSearchOpen(true);
    setIsMenuOpen(false);
    setIsShopDropdownOpen(false);
    setIsProfileDropdownOpen(false);
  };

  const handleSearchClose = () => {
    setIsSearchOpen(false);
    setSearchQuery('');
  };

  const handleWishlistClick = () => {
    setIsMenuOpen(false); // Close mobile menu if open
    setIsSearchOpen(false); // Close search if open
    navigate('/wishlist'); // Navigate to wishlist page
  };

  // Add this useEffect to listen for openCart event
  useEffect(() => {
    const handleOpenCart = () => {
      setIsCartOpen(true);
    };

    window.addEventListener('openCart', handleOpenCart);
    
    return () => {
      window.removeEventListener('openCart', handleOpenCart);
    };
  }, []);

  const handleSearch = (query) => {
    if (!query.trim()) return;
    
    // Close search overlay
    setIsSearchOpen(false);
    setSearchQuery('');
    
    // Navigate to products page with search query
    navigate(`/products?search=${encodeURIComponent(query.trim())}`);
  };

  // Add this to handle form submission
  const handleSearchSubmit = (e) => {
    e.preventDefault();
    handleSearch(searchQuery);
  };

  return (
    <>
      <nav className="bg-white shadow-md fixed w-full top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Regular Navbar Content */}
            <AnimatePresence>
              {!isSearchOpen && (
                <motion.div 
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="flex items-center justify-between w-full"
                >
                  {/* Left Section - Logo */}
                  <div className="flex-shrink-0">
                    <Link to="/" className="block">
                      {loadingStates.logo ? (
                        <div className="h-16 w-40 bg-gray-200 rounded animate-pulse" />
                      ) : (
                        <img 
                          src={logo || `${window.location.origin}/Panchali_logo.png`} 
                          alt="Panchali" 
                          className="h-16 w-auto object-contain"
                          onError={(e) => {
                            // Fallback if image fails to load
                            e.target.src = `${window.location.origin}/Panchali_logo.png`;
                          }}
                        />
                      )}
                    </Link>
                  </div>

                  {/* Center Section - Navigation Links */}
                  <div className="hidden md:flex items-center justify-center flex-1">
                    <div className="flex space-x-8">
                      <Link 
                        to="/new-arrivals" 
                        className="text-gray-900 hover:text-gray-600 px-3 py-2 text-sm font-medium"
                      >
                        NEW IN
                      </Link>
                      <Link 
                        to="/brands" 
                        className="text-gray-900 hover:text-gray-600 px-3 py-2 text-sm font-medium"
                      >
                        MULTIBRANDS
                      </Link>
                      
                      {/* Shop Dropdown */}
                      <div className="relative group">
                        <button 
                          className="text-gray-900 group-hover:text-gray-600 px-3 py-2 text-sm font-medium inline-flex items-center"
                          onClick={() => setIsShopDropdownOpen(!isShopDropdownOpen)}
                        >
                          <span>SHOP</span>
                          <FiChevronDown className="ml-1 h-4 w-4 transition-transform duration-200 group-hover:rotate-180" />
                        </button>
                        
                        {/* Mega Menu Dropdown */}
                        <div 
                          className="absolute left-1/2 transform -translate-x-1/2 mt-3 w-screen max-w-7xl bg-white shadow-xl border-t opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300"
                          style={{ 
                            left: '50%',
                            marginLeft: 'auto',
                            marginRight: 'auto'
                          }}
                        >
                          <div className="container mx-auto">
                            <div className="grid grid-cols-2 p-8 gap-x-16">
                              {/* Collections Section */}
                              <div>
                                <h3 className="text-lg font-medium text-gray-900 mb-6 border-b pb-2">Collections</h3>
                                <div className="grid grid-cols-2 gap-x-8 gap-y-3">
                                  {!loadingStates.nav && shopDropdownData.collections.map((collection, index) => (
                                    <Link
                                      key={index}
                                      to={collection.link}
                                      className="text-gray-600 hover:text-indigo-600 transition-colors text-sm group flex items-center"
                                    >
                                      <span className="group-hover:translate-x-1 transition-transform duration-200">
                                        {collection.name}
                                      </span>
                                    </Link>
                                  ))}
                                </div>
                              </div>
                              
                              {/* Categories Section */}
                              <div>
                                <h3 className="text-lg font-medium text-gray-900 mb-6 border-b pb-2">Categories</h3>
                                <div className="grid grid-cols-2 gap-x-8 gap-y-3">
                                  {!loadingStates.nav && shopDropdownData.categories.map((category, index) => (
                                    <Link
                                      key={index}
                                      to={category.link}
                                      className="text-gray-600 hover:text-indigo-600 transition-colors text-sm group flex items-center"
                                    >
                                      <span className="group-hover:translate-x-1 transition-transform duration-200">
                                        {category.name}
                                      </span>
                                    </Link>
                                  ))}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <Link 
                        to="/offers" 
                        className="text-gray-900 hover:text-gray-600 px-3 py-2 text-sm font-medium"
                      >
                        OFFERS
                      </Link>
                    </div>
                  </div>

                  {/* Right Section - Icons */}
                  <div className="flex items-center space-x-3">
                    <button 
                      onClick={handleSearchOpen}
                      className="p-1.5 text-gray-900 hover:text-gray-600 transition-colors"
                    >
                      <FiSearch className="h-5 w-5" />
                    </button>

                    {/* Wishlist Icon */}
                    <button 
                      onClick={handleWishlistClick}
                      className="p-1.5 text-gray-900 hover:text-gray-600 transition-colors relative"
                    >
                      <FiHeart className="h-5 w-5" />
                      {wishlistCount > 0 && (
                        <span className="absolute -top-1 -right-1 bg-red-600 text-white text-xs w-4 h-4 rounded-full flex items-center justify-center">
                          {wishlistCount}
                        </span>
                      )}
                    </button>

                    {/* Cart Icon */}
                    <button 
                      onClick={() => setIsCartOpen(true)}
                      className="p-1.5 text-gray-900 hover:text-gray-600 transition-colors relative"
                    >
                      <FiShoppingCart className="h-5 w-5" />
                      {/* Dynamic cart count badge */}
                      {cartCount > 0 && (
                        <span className="absolute -top-1 -right-1 bg-red-600 text-white text-xs w-4 h-4 rounded-full flex items-center justify-center">
                          {cartCount}
                        </span>
                      )}
                    </button>

                    {/* Profile Dropdown */}
                    <div className="relative">
                      {/* Mobile View - User Icon */}
                      <button
                        onClick={() => isAuthenticated ? setIsProfileDropdownOpen(!isProfileDropdownOpen) : navigate('/login')}
                        className="md:hidden p-2 text-gray-900 hover:bg-gray-50 rounded-xl transition-all duration-200"
                      >
                        {isAuthenticated ? <FiUser className="h-5 w-5" /> : <FiLogIn className="h-5 w-5" />}
                      </button>

                      {/* Desktop View - Profile Dropdown Button or Login Button */}
                      {isAuthenticated ? (
                        <button
                          onClick={() => setIsProfileDropdownOpen(!isProfileDropdownOpen)}
                          className="hidden md:flex p-2 text-gray-900 hover:bg-gray-50 rounded-xl transition-all duration-200 items-center group"
                        >
                          <span className="text-sm font-medium ml-3">
                            {userProfile.full_name || 'My Account'}
                          </span>
                          <FiChevronDown 
                            className={`ml-2 h-4 w-4 text-gray-600 transition-transform duration-300 ${
                              isProfileDropdownOpen ? 'rotate-180' : ''
                            }`} 
                          />
                        </button>
                      ) : (
                        <Link
                          to="/login"
                          className="hidden md:flex p-2 text-gray-900 hover:bg-gray-50 rounded-xl transition-all duration-200 items-center"
                        >
                          <FiLogIn className="h-5 w-5" />
                          <span className="text-sm font-medium ml-2">Login</span>
                        </Link>
                      )}

                      {/* Profile dropdown menu - Only show when authenticated */}
                      <AnimatePresence>
                        {isAuthenticated && isProfileDropdownOpen && (
                          <>
                            <motion.div
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              exit={{ opacity: 0 }}
                              className="fixed inset-0 z-30"
                              onClick={() => setIsProfileDropdownOpen(false)}
                            />

                            <motion.div
                              initial={{ opacity: 0, scale: 0.95, y: -10 }}
                              animate={{ opacity: 1, scale: 1, y: 0 }}
                              exit={{ opacity: 0, scale: 0.95, y: -10 }}
                              transition={{ 
                                type: "spring",
                                stiffness: 350,
                                damping: 25
                              }}
                              className="absolute right-0 mt-3 w-[320px] bg-white rounded-2xl shadow-xl ring-1 ring-black/5 z-40 overflow-hidden"
                            >
                              {/* User Profile Header */}
                              <div className="p-6 bg-gradient-to-r from-indigo-50 to-purple-50">
                                <div className="flex items-center justify-center space-x-4">
                                  <div>
                                    <h3 className="text-lg font-semibold text-gray-900">
                                      {userProfile.full_name}
                                    </h3>
                                    <p className="text-sm text-gray-600">
                                      {userProfile.email}
                                    </p>
                                  </div>
                                </div>
                              </div>

                              {/* Menu Items */}
                              <div className="p-2">
                                <div className="px-2 py-1.5 text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Account
                                </div>
                                
                                <Link to="/profile" className="flex items-center space-x-3 px-4 py-3 text-gray-700 hover:bg-gray-50 rounded-xl transition-colors">
                                  <FiUser className="h-5 w-5 text-gray-500" />
                                  <span className="font-medium">Profile Settings</span>
                                </Link>
                                
                                <Link to="/orders" className="flex items-center space-x-3 px-4 py-3 text-gray-700 hover:bg-gray-50 rounded-xl transition-colors">
                                  <FiShoppingBag className="h-5 w-5 text-gray-500" />
                                  <span className="font-medium">My Orders</span>
                                </Link>

                                <div className="my-2 border-t border-gray-100" />
                                
                                <button
                                  onClick={handleLogoutClick}
                                  className="w-full flex items-center space-x-3 px-4 py-3 text-red-600 hover:bg-red-50 rounded-xl transition-colors"
                                >
                                  <FiLogOut className="h-5 w-5" />
                                  <span className="font-medium">Sign Out</span>
                                </button>
                              </div>
                            </motion.div>
                          </>
                        )}
                      </AnimatePresence>
                    </div>
                    
                    {/* Mobile Menu Button */}
                    <button
                      onClick={() => setIsMenuOpen(!isMenuOpen)}
                      className="md:hidden p-1.5 text-gray-900 hover:text-gray-600"
                    >
                      {isMenuOpen ? (
                        <FiX className="h-5 w-5" />
                      ) : (
                        <FiMenu className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Advanced Search Bar */}
            <AnimatePresence>
              {isSearchOpen && (
                <motion.div 
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  className="absolute inset-x-0 top-0 bg-white z-50"
                >
                  <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20">
                    <div className="flex items-center h-full">
                      <div className="flex-1 relative">
                        <form onSubmit={handleSearchSubmit}>
                          <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                          <input
                            type="text"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder="Search for products, categories, or brands..."
                            className="w-full pl-12 pr-4 py-3 border-b-2 border-gray-200 focus:border-indigo-500 text-lg bg-transparent outline-none transition-colors"
                            autoFocus
                          />
                        </form>
                      </div>
                      <button
                        onClick={handleSearchClose}
                        className="ml-4 p-2 text-gray-500 hover:text-gray-700 transition-colors"
                      >
                        <FiX className="h-6 w-6" />
                      </button>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* Mobile Navigation Menu */}
        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden border-t border-gray-200"
            >
              <div className="px-4 py-3 space-y-1">
                <Link 
                  to="/new-arrivals"
                  className="block px-3 py-2 text-base font-medium text-gray-900 hover:bg-gray-100 rounded-md"
                >
                  New In
                </Link>
                <Link 
                  to="/brands"
                  className="block px-3 py-2 text-base font-medium text-gray-900 hover:bg-gray-100 rounded-md"
                >
                  Multibrands
                </Link>

                {/* Collections Dropdown */}
                <div className="relative">
                  <button
                    onClick={() => setMobileCollectionsOpen(!mobileCollectionsOpen)}
                    className="w-full flex justify-between items-center px-3 py-2 text-base font-medium text-gray-900 hover:bg-gray-100 rounded-md"
                  >
                    <span>Collections</span>
                    <FiChevronDown className={`transition-transform duration-200 ${mobileCollectionsOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {mobileCollectionsOpen && (
                    <div className="px-4 py-2 bg-gray-50">
                      {shopDropdownData.collections.map((collection, index) => (
                        <Link
                          key={index}
                          to={collection.link}
                          className="block py-2 text-sm text-gray-700 hover:text-indigo-600"
                        >
                          {collection.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>

                {/* Categories Dropdown */}
                <div className="relative">
                  <button
                    onClick={() => setMobileCategoriesOpen(!mobileCategoriesOpen)}
                    className="w-full flex justify-between items-center px-3 py-2 text-base font-medium text-gray-900 hover:bg-gray-100 rounded-md"
                  >
                    <span>Categories</span>
                    <FiChevronDown className={`transition-transform duration-200 ${mobileCategoriesOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {mobileCategoriesOpen && (
                    <div className="px-4 py-2 bg-gray-50">
                      {shopDropdownData.categories.map((category, index) => (
                        <Link
                          key={index}
                          to={category.link}
                          className="block py-2 text-sm text-gray-700 hover:text-indigo-600"
                        >
                          {category.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>

                <Link 
                  to="/offers"
                  className="block px-3 py-2 text-base font-medium text-gray-900 hover:bg-gray-100 rounded-md"
                >
                  Offers
                </Link>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>

      {/* Add CartSidebar component here */}
      <CartSidebar 
        isOpen={isCartOpen} 
        onClose={() => setIsCartOpen(false)} 
      />

      {/* Add spacing below navbar */}
      <div className="h-16" />

      {/* Logout Confirmation Modal */}
      <AnimatePresence>
        {showLogoutConfirm && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center"
              onClick={() => setShowLogoutConfirm(false)}
            >
              {/* Modal */}
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="w-full max-w-sm mx-4 bg-white rounded-xl shadow-xl overflow-hidden"
                onClick={e => e.stopPropagation()} // Prevent closing when clicking modal
              >
                {/* Decorative top border */}
                <div className="h-1 w-full bg-gradient-to-r from-red-500 to-red-600" />
                
                <div className="p-6">
                  <div className="flex flex-col items-center text-center">
                    {/* Icon */}
                    <div className="mb-4 p-3 rounded-full bg-red-50">
                      <FiLogOut className="h-6 w-6 text-red-600" />
                    </div>

                    {/* Content */}
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">
                      Confirm Logout
                    </h3>

                    <p className="text-sm text-gray-600 mb-6">
                      Are you sure you want to log out of your account?
                    </p>

                    {/* Buttons */}
                    <div className="flex gap-3 w-full">
                      <button
                        onClick={() => setShowLogoutConfirm(false)}
                        className="flex-1 px-4 py-2.5 rounded-lg border border-gray-200 text-gray-700 text-sm font-medium hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-200"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleConfirmLogout}
                        className="flex-1 px-4 py-2.5 rounded-lg bg-gradient-to-r from-red-600 to-red-500 text-white text-sm font-medium hover:from-red-700 hover:to-red-600 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                      >
                        Logout
                      </button>
                    </div>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;





































































































